import { motion } from "framer-motion";
import { FiGithub, FiLinkedin, FiMail } from "react-icons/fi";
import profilePhoto from "../../assets/images/profile-photo.jpg";
import Galaxy from "../ui/Galaxy";
import SplitText from "../ui/SplitText";

export default function HeroSection() {
  return (
    <section className="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden">
      {/* Galaxy background */}
      <div className="absolute inset-0 z-0">
        <Galaxy
          mouseRepulsion={false}
          mouseInteraction
          density={2.5}
          glowIntensity={0.2}
          saturation={0.2}
          hueShift={140}
          twinkleIntensity={0.6}
          rotationSpeed={0.1}
          repulsionStrength={2}
          autoCenterRepulsion={0}
          starSpeed={0.5}
          speed={0.7}
        />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 py-20 flex flex-col-reverse md:flex-row items-center gap-12">
        {/* Text */}
        <div className="flex-1 text-brightText">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="text-accentBlue font-mono text-sm mb-2"
          >
            {">"} Hello, I'm
            <span className="animate-pulse ml-1">_</span>
          </motion.p>

          <SplitText
            text="Abdelrahman Helal"
            tag="h1"
            className="text-4xl md:text-6xl font-bold mb-4 text-brightText"
            delay={40}
            duration={0.6}
            from={{ opacity: 0, y: 30 }}
            to={{ opacity: 1, y: 0 }}
            textAlign="left"
            threshold={0.1}
          />

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="text-dimText max-w-lg leading-relaxed mb-8 font-mono text-sm"
          >
            Physicist by training. ML engineer by obsession. Graduating from
            Minerva University with a double major in Physics and Data Science
            &amp; Statistics. I built models that decode the universe — from dark
            matter halos to neutrino signals — and I'm passionate about building
            for the public good.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="flex gap-5"
          >
            <a
              href="https://github.com/abdelrahmanhelal"
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 border border-terminalBorder rounded-full hover:border-accentBlue hover:text-accentBlue hover:shadow-[0_0_15px_rgba(125,211,252,0.3)] transition-all duration-300"
              aria-label="GitHub"
            >
              <FiGithub size={22} />
            </a>
            <a
              href="https://linkedin.com/in/abdelrahman-helal"
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 border border-terminalBorder rounded-full hover:border-neonBlue hover:text-neonBlue hover:shadow-[0_0_15px_rgba(0,212,255,0.3)] transition-all duration-300"
              aria-label="LinkedIn"
            >
              <FiLinkedin size={22} />
            </a>
            <a
              href="mailto:abdelrahman@uni.minerva.edu"
              className="p-3 border border-terminalBorder rounded-full hover:border-neonPurple hover:text-neonPurple hover:shadow-[0_0_15px_rgba(168,85,247,0.3)] transition-all duration-300"
              aria-label="Email"
            >
              <FiMail size={22} />
            </a>
          </motion.div>
        </div>

        {/* Photo */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="flex-shrink-0"
        >
          <div className="w-56 h-56 md:w-72 md:h-72 rounded-full overflow-hidden ring-4 ring-accentBlue/30 shadow-[0_0_30px_rgba(125,211,252,0.15)]">
            <img
              src={profilePhoto}
              alt="Abdelrahman Helal"
              className="w-full h-full object-cover"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
